import React, { useState } from 'react';
import { Home, Mail, Lock, User, ArrowRight, ShieldCheck, Phone } from 'lucide-react';
import logo from '../assets/logo.png';

const Signup = ({ onNavigate }) => {
  const [loading, setLoading] = useState(false);

  const handleSignup = (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate signup delay
    setTimeout(() => {
      setLoading(false);
      onNavigate && onNavigate('home');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center px-4 py-12">
      <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row-reverse">

        {/* Right Side - Visual */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-bl from-emerald-600 to-teal-700 text-white p-12 flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1595856754020-569d4d291e0a?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-8">
              <img src={logo} alt="Logo" className="w-10 h-10 rounded-lg shadow-lg" />
              <span className="text-xl font-bold tracking-tight">AgriScan AI</span>
            </div>
            <h2 className="text-4xl font-black mb-6 leading-tight">Join the<br />Revolution</h2>
            <p className="text-emerald-100 text-lg">Start protecting your crops with AI today. It's free and easy to get started.</p>
          </div>

          <div className="relative z-10 space-y-4 mt-12">
            <div className="flex items-center space-x-3 text-emerald-50">
              <ShieldCheck className="w-6 h-6 text-emerald-300" />
              <span className="font-semibold">Secure Data Protection</span>
            </div>
            <div className="flex items-center space-x-3 text-emerald-50">
              <User className="w-6 h-6 text-emerald-300" />
              <span className="font-semibold">Community Access</span>
            </div>
          </div>
        </div>

        {/* Left Side - Form */}
        <div className="w-full md:w-1/2 p-8 sm:p-12 bg-white">
          <div className="text-right mb-4">
            <button onClick={() => onNavigate && onNavigate('home')} className="text-slate-400 hover:text-emerald-600 transition-colors flex items-center justify-end ml-auto">
              <Home className="w-4 h-4 mr-1" /> Home
            </button>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Create Account</h3>
            <p className="text-slate-500">Join our community of smart farmers.</p>
          </div>

          <button type="button" className="w-full flex items-center justify-center space-x-3 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors mb-6">
            {/* Google Logo SVG */}
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
            <span className="font-semibold text-slate-700">Continue with Google</span>
          </button>

          <div className="relative text-center mb-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100"></div></div>
            <div className="relative inline-block bg-white px-2 text-xs text-slate-400">OR</div>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <div className="relative">
                <input type="text" placeholder="Full Name" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-medium text-slate-800" />
                <User className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
              </div>
            </div>

            <div>
              <div className="relative">
                <input type="email" placeholder="Email Address" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-medium text-slate-800" />
                <Mail className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
              </div>
            </div>

            <div>
              <div className="relative">
                <input type="tel" placeholder="Mobile Number" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-medium text-slate-800" />
                <Phone className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
              </div>
            </div>

            <div>
              <div className="relative">
                <input type="password" placeholder="Password" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-medium text-slate-800" />
                <Lock className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
              </div>
            </div>

            <div className="flex items-center mt-2">
              <input type="checkbox" className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500" />
              <span className="ml-2 text-xs text-slate-500">
                I agree to the
                {' '}
                <button type="button" className="text-emerald-600 font-bold">Terms</button>
                {' '}
                and
                {' '}
                <button type="button" className="text-emerald-600 font-bold">Privacy Policy</button>
              </span>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-xl font-bold shadow-lg shadow-emerald-200 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center">
              {loading ? 'Creating Account...' : 'Sign Up'} <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </form>

          <div className="text-center mt-6">
            <p className="text-slate-500 text-sm">
              Already have an account? {' '}
              <button onClick={() => onNavigate && onNavigate('login')} className="text-emerald-600 font-bold hover:underline">Log In</button>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Signup;
